import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TextField } from '@/components/TextField'
import { Switch } from '@/components/ui/switch'
import { cn, debounce } from '@/lib/utils'

const CREDIT_INPUT_DEBOUNCE_MS = 300
interface CreditToUseSectionProps {
  creditBalanceUSD: number
  creditToUseUSD: number
  maxCreditToUse: number
  setCreditToUseUSD: (value: number) => void
}

export function CreditToUseSection({ creditBalanceUSD, creditToUseUSD, maxCreditToUse, setCreditToUseUSD }: CreditToUseSectionProps) {
  const { t } = useTranslation()
  const [isUsingCredit, setIsUsingCredit] = useState(() => (creditToUseUSD ?? 0) > 0)
  const [inputValue, setInputValue] = useState(creditToUseUSD)
  const pendingCreditRef = useRef<number | null>(null)

  useEffect(() => {
    if (pendingCreditRef.current === null) {
      setInputValue(creditToUseUSD)
      return
    }
    if (creditToUseUSD === pendingCreditRef.current) {
      pendingCreditRef.current = null
      return
    }
    setInputValue(creditToUseUSD)
    pendingCreditRef.current = null
  }, [creditToUseUSD])

  const clampCredit = useCallback((value: number) => Math.min(Math.max(0, value), maxCreditToUse), [maxCreditToUse])

  const debouncedCreditUpdate = useMemo(
    () =>
      debounce((value: number) => {
        const clamped = clampCredit(value)
        pendingCreditRef.current = clamped
        setCreditToUseUSD(clamped)
      }, CREDIT_INPUT_DEBOUNCE_MS),
    [clampCredit, setCreditToUseUSD]
  )

  useEffect(() => () => debouncedCreditUpdate.cancel(), [debouncedCreditUpdate])

  return (
    <div className='border-border-default flex flex-col gap-4 rounded-lg border bg-white p-4 shadow-sm'>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <Switch
            id='credit-use-toggle'
            checked={isUsingCredit}
            onCheckedChange={checked => {
              debouncedCreditUpdate.cancel()
              pendingCreditRef.current = null
              setIsUsingCredit(checked)
              if (checked) {
                const value = clampCredit(maxCreditToUse)
                setInputValue(value)
                setCreditToUseUSD(value)
              } else {
                setInputValue(0)
                setCreditToUseUSD(0)
              }
            }}
          />
          <label className='text-text-primary text-sm font-medium whitespace-nowrap' htmlFor='credit-use-toggle'>
            {t('payment.creditToggleLabel')}
          </label>
        </div>
        <p className='text-text-secondary text-sm'>{t('payment.creditAvailableInline', { amount: creditBalanceUSD })}</p>
      </div>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none',
          isUsingCredit ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className={cn('min-h-0 overflow-hidden', !isUsingCredit && 'pointer-events-none')}>
          <div className='space-y-3'>
            <TextField
              min={0}
              max={maxCreditToUse}
              step={0.01}
              inputMode='decimal'
              value={inputValue}
              disabled={!isUsingCredit}
              onChange={e => {
                const value = clampCredit(parseFloat(e.target.value) || 0)
                setInputValue(value)
                debouncedCreditUpdate(value)
              }}
              trailingIcon={<span className='text-text-secondary text-sm font-medium'>$</span>}
              className='w-full'
            />
          </div>
        </div>
      </div>
    </div>
  )
}
